import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Plus, X } from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiContratos } from "@/componentes/kpi/KpiTalento"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { paraElServidor } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"
import {
  type Contrato,
  cuandoSePaga,
  DIAS_SEMANA,
  FRECUENCIAS,
  type Frecuencia,
} from "@/paginas/interno/nomina/tipos"

type Persona = { id: number; nombres: string; apellidos: string; estado: string }
type Proveedor = { id: number; nombre: string }
type Divisa = { id: number; codigo: string; nombre: string }

/**
 * Los contratos de nómina: quién cobra cuánto, cada cuánto, y a qué ficha se le paga.
 *
 * ⚠ **El sueldo es el de CADA pago, no el del mes.** Una quincena de 450 al mes son
 * 225 aquí, igual que en un gasto programado: el recibo es por período y hay dos al
 * mes. Escribir el mensual pagaría el doble, así que la pantalla lo dice en el campo.
 *
 * ⚠ **Persona y proveedor son dos fichas y las dos hacen falta.** La persona es el
 * empleado —su cargo, su carnet—; el proveedor es a quien la orden de pago le
 * transfiere. Este contrato es el puente que faltaba entre las dos.
 */
export function Contratos() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [creando, setCreando] = useState(false)
  const [cerrando, setCerrando] = useState<Contrato | null>(null)
  const puedeAdministrar = tienePermiso(usuario, "nomina:administrar")
  const vista = useRef<HTMLDivElement | null>(null)

  const contratos = useQuery({
    queryKey: ["nomina-contratos"],
    queryFn: () => api.get<Contrato[]>("/api/v1/nomina/contratos"),
  })

  const crear = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post<Contrato>("/api/v1/nomina/contratos", cuerpo),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["nomina-contratos"] })
      setCreando(false)
    },
  })

  const cerrar = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      api.post(`/api/v1/nomina/contratos/${id}/cerrar`, { motivo }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["nomina-contratos"] })
      setCerrando(null)
    },
  })

  if (contratos.isPending) return <p className="py-6">Cargando…</p>
  if (contratos.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar los contratos de nómina.
      </p>
    )
  }

  const filas = contratos.data

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {filas.length} {filas.length === 1 ? "contrato vivo" : "contratos vivos"}
        </p>
        <div className="flex flex-wrap items-start gap-2">
          <Exportar
            titulo="Contratos de nómina"
            subtitulo={`${filas.length} ${filas.length === 1 ? "contrato vivo" : "contratos vivos"}`}
            contenido={vista}
          />
          {puedeAdministrar && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              <Plus size={16} /> Nuevo contrato
            </Boton>
          )}
        </div>
      </div>
      <KpiContratos />

      <div ref={vista}>
        <Tabla etiqueta="Contratos de nómina">
          <Cabecera>
            <Th>Empleado</Th>
            <Th>Se le paga a</Th>
            <Th derecha>Sueldo por pago</Th>
            <Th>Cuándo</Th>
            <Th>Conceptos fijos</Th>
            <Th />
          </Cabecera>
          <Cuerpo>
            {filas.length === 0 && (
              <TablaVacia colSpan={6}>
                Todavía no hay contratos. Sin ellos, un período de nómina sale vacío.
              </TablaVacia>
            )}
            {filas.map((c) => (
              <Fila key={c.id} atenuada={!c.activo}>
                <Td>{c.persona ?? "—"}</Td>
                <Td>{c.proveedor ?? "—"}</Td>
                <Td
                  derecha
                  numero
                  data-exportar-valor={`${monto(c.sueldo_base)} ${c.divisa ?? ""}`.trim()}
                >
                  {c.sueldo_base} {c.divisa ?? ""}
                </Td>
                <Td>{cuandoSePaga(c)}</Td>
                <Td data-exportar-valor={c.conceptos.map((a) => a.concepto).join(", ") || "—"}>
                  {c.conceptos.length === 0 ? (
                    <span style={{ color: "var(--fg-muted)" }}>—</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {c.conceptos.map((a) => (
                        <Insignia key={a.id} tono={a.tipo === "DEDUCCION" ? "aviso" : "info"}>
                          {a.concepto}
                        </Insignia>
                      ))}
                    </span>
                  )}
                </Td>
                <Td>
                  {puedeAdministrar && c.activo && (
                    <button
                      type="button"
                      aria-label={`Cerrar el contrato de ${c.persona ?? "esta persona"}`}
                      style={{ color: "var(--fg-muted)" }}
                      onClick={() => setCerrando(c)}
                    >
                      <X size={14} />
                    </button>
                  )}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </div>

      <Modal
        abierto={creando}
        titulo="Nuevo contrato de nómina"
        descripcion="El sueldo es el de cada pago, no el del mes."
        onCerrar={() => setCreando(false)}
      >
        {creando && (
          <FormularioContrato
            enviando={crear.isPending}
            error={crear.error instanceof ErrorApi ? crear.error.message : null}
            onEnviar={(cuerpo) => crear.mutate(cuerpo)}
            onCancelar={() => setCreando(false)}
          />
        )}
      </Modal>

      <Modal
        abierto={cerrando !== null}
        titulo="Cerrar el contrato"
        descripcion="No se borra: sigue explicando los recibos que ya firmó."
        onCerrar={() => setCerrando(null)}
      >
        {cerrando && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              const motivo = String(new FormData(e.currentTarget).get("motivo") ?? "").trim()
              if (motivo.length >= 3) cerrar.mutate({ id: cerrando.id, motivo })
            }}
          >
            <Campo para="cierre-motivo" etiqueta="Motivo" ayuda="Queda en la bitácora.">
              <input id="cierre-motivo" name="motivo" className="campo mt-1" required />
            </Campo>
            <div className="flex justify-end gap-2">
              <Boton variante="fantasma" onClick={() => setCerrando(null)}>
                Cancelar
              </Boton>
              <Boton type="submit" variante="peligro" disabled={cerrar.isPending}>
                Cerrar contrato
              </Boton>
            </div>
          </form>
        )}
      </Modal>
    </section>
  )
}

function FormularioContrato({
  enviando,
  error,
  onEnviar,
  onCancelar,
}: {
  enviando: boolean
  error: string | null
  onEnviar: (cuerpo: Record<string, unknown>) => void
  onCancelar: () => void
}) {
  const [personaId, setPersonaId] = useState<number | null>(null)
  const [proveedorId, setProveedorId] = useState<number | null>(null)
  const [divisaId, setDivisaId] = useState<number | null>(null)
  const [frecuencia, setFrecuencia] = useState<Frecuencia>("QUINCENAL")

  const personas = useQuery({
    queryKey: ["personas", "empleados"],
    // ⚠ `/personas` devuelve una PÁGINA (`{items, total}`), no una lista: leerla como
    // lista tumbaba la pantalla con «.map is not a function» (10/10).
    queryFn: async () =>
      (await api.get<{ items: Persona[] }>("/api/v1/personas?limit=100&personal=true")).items,
  })
  const proveedores = useQuery({
    queryKey: ["proveedores", "ADMINISTRATIVO"],
    queryFn: () =>
      api.get<Proveedor[]>("/api/v1/proveedores?limit=100&activo=true&categoria=ADMINISTRATIVO"),
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
  })

  const semanal = frecuencia === "SEMANAL"

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        const datos = new FormData(e.currentTarget)
        const texto = (campo: string) => String(datos.get(campo) ?? "").trim()
        if (personaId === null || proveedorId === null || divisaId === null) return
        onEnviar({
          persona_id: personaId,
          proveedor_id: proveedorId,
          divisa_id: divisaId,
          sueldo_base: paraElServidor(texto("sueldo_base")),
          frecuencia,
          // La semanal no tiene día del mes y las otras no tienen día de la semana:
          // mandar los dos guardaría un dato que ninguna cuenta lee.
          dia_pago: semanal ? null : Number(texto("dia_pago") || "15"),
          dia_semana: semanal ? Number(texto("dia_semana") || "5") : null,
        })
      }}
    >
      <Campo para="ct-persona" etiqueta="Empleado">
        <SelectorBuscable
          id="ct-persona"
          opciones={(personas.data ?? []).map((p) => ({
            valor: p.id,
            etiqueta: `${p.nombres} ${p.apellidos}`,
          }))}
          valor={personaId}
          onCambiar={setPersonaId}
        />
      </Campo>

      <Campo
        para="ct-proveedor"
        etiqueta="Se le paga a"
        ayuda="Su ficha administrativa: es a quien la orden de pago le transfiere."
      >
        <SelectorBuscable
          id="ct-proveedor"
          opciones={(proveedores.data ?? []).map((p) => ({ valor: p.id, etiqueta: p.nombre }))}
          valor={proveedorId}
          onCambiar={setProveedorId}
        />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          para="ct-sueldo"
          etiqueta="Sueldo por pago"
          ayuda="Lo de CADA pago, no lo del mes: una quincena de 450 al mes son 225."
        >
          <input
            id="ct-sueldo"
            name="sueldo_base"
            inputMode="decimal"
            required
            className="campo mt-1 tabular-nums"
          />
        </Campo>
        <Campo para="ct-divisa" etiqueta="Moneda">
          <SelectorBuscable
            id="ct-divisa"
            opciones={(divisas.data ?? []).map((d) => ({
              valor: d.id,
              etiqueta: `${d.codigo} — ${d.nombre}`,
            }))}
            valor={divisaId}
            onCambiar={setDivisaId}
          />
        </Campo>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo para="ct-frecuencia" etiqueta="Frecuencia">
          <select
            id="ct-frecuencia"
            value={frecuencia}
            onChange={(e) => setFrecuencia(e.target.value as Frecuencia)}
            className="campo mt-1"
          >
            {FRECUENCIAS.map((f) => (
              <option key={f.valor} value={f.valor}>
                {f.etiqueta}
              </option>
            ))}
          </select>
        </Campo>

        {semanal ? (
          <Campo para="ct-dia-semana" etiqueta="Día de la semana">
            <select id="ct-dia-semana" name="dia_semana" defaultValue={5} className="campo mt-1">
              {DIAS_SEMANA.map((d) => (
                <option key={d.valor} value={d.valor}>
                  {d.etiqueta}
                </option>
              ))}
            </select>
          </Campo>
        ) : (
          <Campo
            para="ct-dia"
            etiqueta={frecuencia === "QUINCENAL" ? "Primer día de pago" : "Día de pago"}
            ayuda={
              frecuencia === "QUINCENAL"
                ? "El segundo pago es siempre el último día del mes."
                : "Un día que no exista en ese mes cae al último."
            }
          >
            <input
              id="ct-dia"
              name="dia_pago"
              inputMode="numeric"
              defaultValue={15}
              className="campo mt-1 tabular-nums"
            />
          </Campo>
        )}
      </div>

      {error && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCancelar}>
          Cancelar
        </Boton>
        <Boton
          type="submit"
          variante="primario"
          disabled={enviando || personaId === null || proveedorId === null || divisaId === null}
        >
          Crear contrato
        </Boton>
      </div>
    </form>
  )
}
